class ScHeaderMenu extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.querySelector(".sc-menu-wrapper");
    this.toggleButtons = this.querySelectorAll("[sc-toggle-menu]");
    this.submenuLinks = this.querySelectorAll("[toggle-submenu]");

    this.toggleButtons.forEach((el) => {
      el.addEventListener("click", () => {
        if (this.wrapper) {
          this.wrapper.classList.toggle("is-open");
        }
      });
    });

    this.submenuLinks.forEach((el) => {
      el.addEventListener("click", (e) => {
        const linkContainer = el.closest(".sc-menu-link");
        const sub = linkContainer.nextElementSibling;

        if (!sub || !sub.classList.contains("sc-menu-sublinks")) return;

        const isOpen = sub.dataset.sublinkState === "1";

        // First click opens submenu; second click navigates to parent URL
        if (isOpen) return;

        e.preventDefault();
        sub.dataset.sublinkState = "1";
        sub.classList.add("menu-sublinks-open");
        sub.style.height = `${sub.scrollHeight}px`;
      });
    });
  }
}

customElements.define("sc-header-menu", ScHeaderMenu)

/* SC FAQ Toggle Button */

class QuestionToggle extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <span class="sc-icon plus"></span>
      <span class="sc-icon minus"></span>
    `;
  }

  connectedCallback() {
    this.faqBlock = this.closest(".faq-block");
    this.faqBlock.addEventListener("click", () => this.toggle());
  }

  toggle() {
    const isOpen = this.classList.contains("open");
    this.classList.toggle("open", !isOpen);
    this.faqBlock.classList.toggle("active", !isOpen);
  }
}

customElements.define("question-toggle", QuestionToggle);


/* SC Product Page */
class QuantityButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick);
  }

  connectedCallback() {
    this.input = this.closest('.product-quantity-quantity').querySelector('.quantity-input');
  }

  handleClick() {
    const type = this.getAttribute('type');
    let currentValue = parseInt(this.input.value) || 1;
    const maxQuantity = parseInt(this.input.getAttribute('max')) || Infinity;

    if (type === 'plus' && currentValue < maxQuantity) {
      this.input.value = currentValue + 1;
    } else if (type === 'minus' && currentValue > 1) {
      this.input.value = currentValue - 1;
    }

    this.input.dispatchEvent(new Event('change'));
  }
}

class AddToCartButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.addToCart);
  }

  connectedCallback() {
    this.quantityInput = this.closest('.sc-product-mash')?.querySelector('.quantity-input')
      || document.querySelector('.quantity-input');
    this.successText = this.getAttribute('data-success');
    this.originalText = this.querySelector('span')?.textContent;
  }

  async addToCart() {
    if (this.classList.contains('sc-load') || this.hasAttribute('disable')) return;

    if (!this.quantityInput) {
      console.error('Quantity input not found');
      return;
    }

    this.classList.add('sc-load');
    const variantId = this.getAttribute('data-variant-id');
    const quantity = parseInt(this.quantityInput.value, 10) || 1;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

    try {
      // Collect section IDs (cart-drawer etc.) so Shopify returns updated HTML
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: Number(variantId),
          quantity,
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.message || 'Failed to add to cart');
      }

      // Refresh full cart state, then attach rendered sections for line items
      const cartContent = await (await fetch(`${root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            cart: cartContent,
          },
        })
      );

      this.querySelector('span').textContent = this.successText;
      setTimeout(() => {
        this.querySelector('span').textContent = this.originalText;
      }, 8000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true })
      );
      this.querySelector('span').textContent = 'Error';
      setTimeout(() => {
        this.querySelector('span').textContent = this.originalText;
      }, 2000);
    } finally {
      this.classList.remove('sc-load');
    }
  }
}

customElements.define('button-quantity', QuantityButton);
customElements.define('button-add-cart', AddToCartButton);

/* SC Product variant picker – one group per product option */
class ScVariantPicker extends HTMLElement {
  connectedCallback() {
    const script = this.querySelector('[data-sc-variants]');
    if (!script) return;

    try {
      this.variants = JSON.parse(script.textContent);
    } catch (error) {
      console.error('Failed to parse product variants', error);
      return;
    }

    this.addToCartBtn = document.querySelector('button-add-cart');
    this.quantityInput = document.querySelector('.quantity-input');
    this.priceEl = document.querySelector('.sc-product-price_price');
    this.gallery = document.querySelector('[data-sc-product-gallery]');

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sc-option-value]');
      if (!button || button.hasAttribute('disable') || button.classList.contains('active')) return;

      const group = button.closest('[data-sc-option-index]');
      if (!group) return;

      group.querySelectorAll('[data-sc-option-value]').forEach((el) => el.classList.remove('active'));
      button.classList.add('active');

      const selectedLabel = group.querySelector('[data-sc-selected-value]');
      if (selectedLabel) selectedLabel.textContent = button.dataset.scOptionValue;

      this.updateVariant();
    });

    this.updateAvailability(this.getSelectedOptions());
    this.syncGallery();
  }

  getSelectedOptions() {
    return Array.from(this.querySelectorAll('[data-sc-option-index]')).map((group) => {
      const active = group.querySelector('.heat-selector.active');
      return active ? active.dataset.scOptionValue : null;
    });
  }

  findVariant(selectedOptions) {
    return this.variants.find((variant) =>
      selectedOptions.every((value, index) => !value || variant.options[index] === value)
    );
  }

  getVariantMediaId(variant) {
    if (!variant) return null;
    return variant.featured_image?.id || variant.featured_media?.id || null;
  }

  syncGallery(variant = this.findVariant(this.getSelectedOptions())) {
    const mediaId = this.getVariantMediaId(variant);
    if (!mediaId || !this.gallery?.scGoToMedia) return;
    this.gallery.scGoToMedia(mediaId);
  }

  updateVariant() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.findVariant(selectedOptions);
    if (!variant) return;

    if (this.addToCartBtn) {
      this.addToCartBtn.setAttribute('data-variant-id', String(variant.id));
      if (variant.available) {
        this.addToCartBtn.removeAttribute('disable');
      } else {
        this.addToCartBtn.setAttribute('disable', '');
      }
    }

    if (this.quantityInput && typeof variant.inventory_quantity === 'number') {
      const maxQty = Math.max(variant.inventory_quantity, 1);
      this.quantityInput.setAttribute('max', String(maxQty));
      if (parseInt(this.quantityInput.value, 10) > maxQty) {
        this.quantityInput.value = String(maxQty);
      }
    }

    if (this.priceEl && typeof variant.price === 'number') {
      this.priceEl.textContent = this.formatMoney(variant.price);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    history.replaceState({}, '', url.toString());

    this.updateAvailability(selectedOptions);
    this.syncGallery(variant);
  }

  updateAvailability(selectedOptions) {
    this.querySelectorAll('[data-sc-option-index]').forEach((group, optionIndex) => {
      group.querySelectorAll('[data-sc-option-value]').forEach((button) => {
        const value = button.dataset.scOptionValue;
        const isAvailable = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant.options[optionIndex] !== value) return false;

          return selectedOptions.every((selectedValue, index) => {
            if (index === optionIndex || !selectedValue) return true;
            return variant.options[index] === selectedValue;
          });
        });

        if (isAvailable) {
          button.removeAttribute('disable');
        } else {
          button.setAttribute('disable', '');
        }
      });
    });
  }

  formatMoney(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const currency = window.Shopify?.currency?.active || 'CHF';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-CH', {
        style: 'currency',
        currency,
      }).format(Number(amount));
    } catch (error) {
      return `${currency} ${amount}`;
    }
  }
}

customElements.define('sc-variant-picker', ScVariantPicker);

class HideOpenFAQ extends HTMLElement {
  constructor() {
    super();
    this.innerHTML = `
      <svg id="Pfeil" xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 30 30">
        <path id="Pfad_37" data-name="Pfad 37" d="M6214.8,1384.942l4.8-4.8,4.8,4.8" transform="translate(-6204.304 -1368.442)" fill="none" stroke="#2e2c27" stroke-width="2"/>
      </svg>
    `;
  }

  connectedCallback() {
    this.faqBlock = this.closest(".faq-block");
    this.faqBlock.addEventListener("click", () => this.toggle());
  }

  toggle() {
    const isOpen = this.classList.contains("open");
    this.classList.toggle("open", !isOpen);
    this.faqBlock.classList.toggle("active", !isOpen);
  }
}

customElements.define("faq-toggle", HideOpenFAQ);

class SizeChartModal extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
      const linkText = this.getAttribute('link-text') || 'Größentabelle';
      const pageContent = this.innerHTML;

      this.shadowRoot.innerHTML = `
        <style>
          .modal {
            position: fixed;
            top: 0; left: 0;
            width: 100%; height: 100%;
            background: rgba(0,0,0,0.5);
            display: none;
            align-items: center;
            justify-content: center;
            z-index: 9999;
          }
          .modal.active {
            display: flex;
          }
          .modal-content {
            background: #fff;
            padding: 2rem;
            max-width: 700px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
            border-radius: 8px;
            position: relative;
          }
          .close {
            position: absolute;
            top: 1rem; right: 1rem;
            cursor: pointer;
            font-size: 1.5rem;
          }
          a.link {
            cursor: pointer;
            text-decoration: underline;
            color: inherit;
          }
        </style>

        <a class="link">${linkText}</a>
        <div class="modal">
          <div class="modal-content">
            <span class="close">&times;</span>
            <div class="content">${pageContent}</div>
          </div>
        </div>
      `;

      const modal = this.shadowRoot.querySelector('.modal');
      const link = this.shadowRoot.querySelector('.link');
      const close = this.shadowRoot.querySelector('.close');

      link.addEventListener('click', e => {
        e.preventDefault();
        modal.classList.add('active');
      });

      close.addEventListener('click', () => modal.classList.remove('active'));
      modal.addEventListener('click', (e) => {
        if (e.target === modal) modal.classList.remove('active');
      });
    }
  }

  customElements.define('size-chart-modal', SizeChartModal);